import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
  List,
  ListItem,
  UnorderedList,
} from '@chakra-ui/react';
import HelpIcon from '../InvestmentScreening/HelpIcon';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import DebouncedInput from '../../components/DebouncedInput';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Messages, InputLimits } from '../../contexts/constants';
import { AccountingOrganizationType, Errors } from '../../contexts/types';
import { formatLocalDateTime, formatYearMonthByLocale } from '../../utils/utils';

/** カラムリサイズハンドル（スプレッドシート方式：対象列のみ幅変更、他列は維持） */
const ResizeHandle: React.FC<{
  colIndex: number;
  setColWidths: React.Dispatch<React.SetStateAction<number[]>>;
}> = ({ colIndex, setColWidths }) => {
  // ドラッグ中にアンマウントされてもリスナーを確実に解除するためrefで保持
  const onMouseMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
  const onMouseUpRef = useRef<((e: MouseEvent) => void) | null>(null);

  useEffect(() => {
    return () => {
      if (onMouseMoveRef.current) document.removeEventListener('mousemove', onMouseMoveRef.current);
      if (onMouseUpRef.current) document.removeEventListener('mouseup', onMouseUpRef.current);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.target as HTMLElement).closest('th');
    if (!th) return;
    const tr = th.closest('tr');
    if (!tr) return;

    // 全カラムの現在のpx幅を取得して固定化
    const ths = Array.from(tr.querySelectorAll('th'));
    const currentWidths = ths.map(t => t.offsetWidth);
    setColWidths(currentWidths);

    const startX = e.clientX;
    const startWidth = currentWidths[colIndex];

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      setColWidths(prev => {
        const next = [...prev];
        next[colIndex] = Math.max(50, startWidth + delta);
        return next;
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      onMouseMoveRef.current = null;
      onMouseUpRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    onMouseMoveRef.current = onMouseMove;
    onMouseUpRef.current = onMouseUp;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <Box
      position="absolute"
      right="-2px"
      top={0}
      bottom={0}
      width="5px"
      cursor="col-resize"
      onMouseDown={handleMouseDown}
      _hover={{ bg: "blue.300" }}
      zIndex={2}
    />
  );
};

/** Excelカラム定義の型 */
type ExcelColumnDef = { key: string; patterns: string[] };

/** 現行形式（組織ID付きフラット）Excelのカラム定義 */
const FLAT_COLUMN_DEFS: ExcelColumnDef[] = [
  { key: 'organizationId', patterns: ['organizationId', '組織ID', 'ID'] },
  { key: 'organizationName', patterns: ['organizationName', '組織名', '名称'] },
  { key: 'organizationNameEn', patterns: ['organizationNameEn', '組織名（英語）', '組織名(英語)', '英語名'] },
  { key: 'parentOrganizationId', patterns: ['parentOrganizationId', '親組織ID', '親ID'] },
];

/** 階層形式（新形式）Excelのカラム定義 */
const HIERARCHY_COLUMN_DEFS: ExcelColumnDef[] = [
  { key: 'businessHeadquartersJa', patterns: ['businessHeadquartersJa', '事業本部'] },
  { key: 'businessHeadquartersEn', patterns: ['businessHeadquartersEn', '事業本部(英語)'] },
  { key: 'departmentJa', patterns: ['departmentJa', '部門'] },
  { key: 'departmentEn', patterns: ['departmentEn', '部門(英語)'] },
  { key: 'businessUnitJa', patterns: ['businessUnitJa', '事業部'] },
  { key: 'businessUnitEn', patterns: ['businessUnitEn', '事業部（英語)'] },
  { key: 'businessSubtotalJa', patterns: ['businessSubtotalJa', 'ﾋﾞｼﾞﾈｽ小計'] },
  { key: 'businessSubtotalEn', patterns: ['businessSubtotalEn', 'ﾋﾞｼﾞﾈｽ(SubTotal)(英語)'] },
  { key: 'businessJa', patterns: ['businessJa', 'ﾋﾞｼﾞﾈｽ'] },
  { key: 'businessEn', patterns: ['businessEn', 'ﾋﾞｼﾞﾈｽ(英語)'] },
  { key: 'productJa', patterns: ['productJa', '製品'] },
  { key: 'productEn', patterns: ['productEn', '製品(英語)'] },
];

type AccountingOrganizationExistType = AccountingOrganizationType;

interface ImportDataType extends AccountingOrganizationType {
  /** 状態 */
  status?: "unmapped" | "new" | "overwrite" | "invalid";
  /** 紐づけ組織ID */
  mappingOrganizationId?: string;
  /** 紐づけ組織名 */
  mappingOrganizationName?: string;
};

/** 無効化候補エントリの型 */
type InvalidateDataType = AccountingOrganizationExistType & {
  /** true のとき無効化をスキップする（ユーザーが「無効化しない」チェックを入れた場合） */
  excluded?: boolean;
};

/**
 * マスタメンテナンス - 管理体系設定 画面
 */
const AccountingOrganization: React.FC = () => {
  const { t } = useTranslation();
  // Toastメッセージを表示するためのフック
  const toast = useToast();
  // WebAPIContextを使用してAPI操作を行う
  const WebAPI = useContext(WebAPIContext);

  /** 件数上限 */
  const MAX_IMPORT_SIZE = 1000;

  /** タイトル */
  const TITLE = t('features.MasterMaintenance.AccountingOrganization.title');
  /** エンティティ略称 */
  const ENTITY = t('features.MasterMaintenance.AccountingOrganization.entityLabel');

  /** 列固定スタイル(2列目以降はleftの設定が必要) */
  const fixedColCellStyle = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
    boxShadow: "1px 0 0 #ccc",
  }

  /** セルはみ出し防止スタイル（tableLayout:fixed と組み合わせて使用） */
  const ellipsisStyle = {
    overflow: "hidden" as const,
    textOverflow: "ellipsis" as const,
    whiteSpace: "nowrap" as const,
  }

  /** 項目（ラベル）スタイル */
  const labelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    marginTop: "8px",
    width: 170,
  }

  /** 項目（値）幅 */
  const valueWidth = 475;

  /** 管理体系マスタデータ */
  const [masterData, setMasterData] = useState<AccountingOrganizationType[]>([]);

  /** 編集対象管理体系マスタ */
  const [editTarget, setEditTarget] = useState<AccountingOrganizationType>({});

  /** 編集前の適用年度（適用年度変更時にPUT判定するため保持） */
  const [originalFiscalPeriod, setOriginalFiscalPeriod] = useState<string>("");

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>({});

  /** モーダル表示制御用：編集用モーダル */
  const { isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();

  /** モーダル表示制御用：プレビュー表示用モーダル */
  const { isOpen: isOpenPreviewModal, onOpen: onOpenPreviewModal, onClose: onClosePreviewModal } = useDisclosure();

  /** モーダル表示制御用：既存データ紐づけ選択モーダル */
  const { isOpen: isOpenMappingModal, onOpen: onOpenMappingModal, onClose: onCloseMappingModal } = useDisclosure();

  /** アラートダイアログ表示制御用：Excel読み込み確認 */

  /** アラートダイアログ表示制御用：過去年度編集確認 */
  const { isOpen: isPastYearAlertOpen, onOpen: onOpenPastYearAlert, onClose: onClosePastYearAlert } = useDisclosure();

  /** Excel一括登録 実行確認ダイアログ表示制御用 */
  const { isOpen: isBulkConfirmOpen, onOpen: onOpenBulkConfirm, onClose: onCloseBulkConfirm } = useDisclosure();
  const bulkConfirmCancelRef = useRef<HTMLButtonElement>(null);

  /** アラートダイアログのキャンセルボタン参照 */
  const cancelRef = useRef<HTMLButtonElement>(null);
  const pastYearCancelRef = useRef<HTMLButtonElement>(null);

  // カラムリサイズ用state（空配列＝初期幅はCSSパーセント、リサイズ開始時にpx化）
  const [colWidths, setColWidths] = useState<number[]>([]);
  // プレビューモーダル タブ1（インポートデータ）のカラムリサイズ用state
  const [previewColWidths, setPreviewColWidths] = useState<number[]>([]);
  // プレビューモーダル タブ2（無効化対象）のカラムリサイズ用state
  const [invalidateColWidths, setInvalidateColWidths] = useState<number[]>([]);
  // 紐づけ設定モーダルのカラムリサイズ用state
  const [mappingColWidths, setMappingColWidths] = useState<number[]>([]);

  // （フィルター）組織IDを管理
  const [filterOrganizationId, setFilterOrganizationId] = useState<string>("");
  // （フィルター）組織名を管理
  const [filterOrganizationName, setFilterOrganizationName] = useState<string>("");
  // （フィルター）組織名（英語）を管理
  const [filterOrganizationNameEn, setFilterOrganizationNameEn] = useState<string>("");
  // （フィルター）親組織IDを管理
  const [filterParentOrganizationId, setFilterParentOrganizationId] = useState<string>("");

  // 適用年度（検索に使用）
  const [selectedFiscalPeriod, setSelectedFiscalPeriod] = useState<string>("");

  // 検索済みかどうか（表示用）
  const [hasSearched, setHasSearched] = useState(false);

  // 検索実行時の適用年度（ラベル表示用）
  const [searchedFiscalPeriod, setSearchedFiscalPeriod] = useState<string>("");

  // 編集モーダル保存ボタンの活性制御
  const [isSaving, setIsSaving] = useState(false);

  // 親組織IDオートコンプリート用
  const [parentOrgInputValue, setParentOrgInputValue] = useState("");
  const [showParentOrgSuggestions, setShowParentOrgSuggestions] = useState(false);
  const parentOrgInputRef = useRef<HTMLInputElement>(null);

  // オートコンプリート用データ（検索結果と異なる適用年度の場合に別途取得）
  const [autocompleteData, setAutocompleteData] = useState<AccountingOrganizationType[]>([]);
  // オートコンプリート用データの取得元適用年度（キャッシュ判定用）
  const [autocompleteFiscalPeriod, setAutocompleteFiscalPeriod] = useState<string>("");

  // Excel読み込み用
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 読み込んだExcelが階層形式かどうか（仮ID表示の切り替えに使用）
  const [isHierarchyFormat, setIsHierarchyFormat] = useState<boolean>(false);
  // インポート候補データ
  const [importData, setImportData] = useState<ImportDataType[]>([]);

  // 既存データ
  const [existingData, setExistingData] = useState<AccountingOrganizationExistType[]>([]);

  // 無効化候補データ（インポートデータに存在しない既存組織）
  const [invalidateData, setInvalidateData] = useState<InvalidateDataType[]>([]);
  // 無効化候補の元IDセット（handleConfirmMappingで復元判定に使用）
  const [originalInvalidateIds, setOriginalInvalidateIds] = useState<Set<string>>(new Set());

  // 紐づけモーダル：操作対象の importData インデックス（-1 は未選択）
  const [mappingTargetIndex, setMappingTargetIndex] = useState<number>(-1);
  // 紐づけモーダル：モーダル内で選択中の既存組織ID（""は「該当なし」）
  const [mappingSelectedId, setMappingSelectedId] = useState<string>("");

  // Excel一括登録確認ダイアログ用の適用年度
  const [batchFiscalPeriod, setBatchFiscalPeriod] = useState<string>("");

  /** オートコンプリートのソースデータ（autocompleteDataがあればそちらを優先） */
  const autocompleteSourceData = useMemo(() => {
    return autocompleteData.length > 0 ? autocompleteData : masterData;
  }, [autocompleteData, masterData]);

  /** 親組織IDオートコンプリート候補（自分自身を除外、入力値でフィルタ） */
  const parentOrgSuggestions = useMemo(() => {
    if (!showParentOrgSuggestions) return [];
    return autocompleteSourceData.filter(m => {
      if (!m.organizationId || m.organizationId === editTarget.organizationId) return false;
      if (m.deleted) return false; // 削除済みは候補から除外
      if (!parentOrgInputValue) return true;
      const search = parentOrgInputValue.toLowerCase();
      return (
        (m.organizationId || "").toLowerCase().includes(search) ||
        (m.organizationName || "").toLowerCase().includes(search) ||
        (m.organizationNameEn || "").toLowerCase().includes(search)
      );
    }).slice(0, 20);
  }, [autocompleteSourceData, editTarget.organizationId, parentOrgInputValue, showParentOrgSuggestions]);

  /** 組織ID → 組織名のMap（親組織名の表示・フィルター用） */
  const orgNameMap = useMemo(() => {
    return new Map(masterData.map(m => [m.organizationId, m.organizationName]));
  }, [masterData]);

  /** importDataの組織ID → 組織名のMap（プレビューモーダルの親組織名表示用） */
  const importOrgNameMap = useMemo(() => {
    return new Map(importData.map(m => [m.organizationId, m.organizationName]));
  }, [importData]);

  /** 階層形式の仮ID → 実ID のMap（プレビューモーダルの親組織ID表示用） */
  const importIdToRealIdMap = useMemo(() => {
    return new Map(
      importData
        .filter((m): m is ImportDataType & { organizationId: string; mappingOrganizationId: string } =>
          m.status === "overwrite" && !!m.organizationId && !!m.mappingOrganizationId
        )
        .map(m => [m.organizationId, m.mappingOrganizationId])
    );
  }, [importData]);

  /** フィルター済みマスタデータ */
  const filteredMasterData = useMemo(() => {
    return (masterData ?? []).filter(m => {
      if (!m) return false;
      const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
        return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
      };
      // 親組織IDまたは親組織名のいずれかが部分一致すればOK
      const parentOrgName = orgNameMap.get(m.parentOrganizationId ?? '') ?? '';
      const matchesParent = hasFilterString(m.parentOrganizationId, filterParentOrganizationId) ||
        hasFilterString(parentOrgName, filterParentOrganizationId);
      return (
        hasFilterString(m.organizationId, filterOrganizationId) &&
        hasFilterString(m.organizationName, filterOrganizationName) &&
        hasFilterString(m.organizationNameEn, filterOrganizationNameEn) &&
        matchesParent
      );
    });
  }, [masterData, orgNameMap, filterOrganizationId, filterOrganizationName, filterOrganizationNameEn, filterParentOrganizationId]);

  /** 削除済み組織IDのセット（親組織IDの警告表示用） */
  const deletedOrgIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of masterData) {
      if (m.deleted && m.organizationId) {
        ids.add(m.organizationId);
      }
    }
    return ids;
  }, [masterData]);

  /** テーブルBody部（メモ化して編集モーダル操作時の再レンダリングを防止） */
  const tableBody = useMemo(() => (
    <Tbody>
      {filteredMasterData.map(record => {
        const bgColor = record.deleted ? "gray.100" : "white";
        const isParentDeleted = !!record.parentOrganizationId && deletedOrgIds.has(record.parentOrganizationId);
        return (
          <Tr key={`${record.organizationId}-${record.fiscalPeriod}`} _hover={{ bg: "gray.50" }}>
            <Td borderRight="1px solid" borderColor="gray.200" bgColor={bgColor} {...ellipsisStyle} {...fixedColCellStyle}>
              <Button
                variant="link"
                textDecoration="underline"
                color="blue.500"
                onClick={(e) => {
                  e.currentTarget.blur();
                  handleEdit(record.organizationId, record.fiscalPeriod)
                }}
                sx={{
                  display: 'inline-block',
                  maxW: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {record.organizationId}
              </Button>
            </Td>
            <Td borderRight="1px solid" borderColor="gray.200" bgColor={bgColor} {...ellipsisStyle}>{record.organizationName}</Td>
            <Td borderRight="1px solid" borderColor="gray.200" bgColor={bgColor} {...ellipsisStyle}>{record.organizationNameEn}</Td>
            <Td borderRight="1px solid" borderColor="gray.200" bgColor={isParentDeleted ? "red.100" : bgColor} color={isParentDeleted ? "red.600" : undefined} {...ellipsisStyle} title={record.parentOrganizationId ? `${orgNameMap.get(record.parentOrganizationId) ?? record.parentOrganizationId} (${record.parentOrganizationId})` : ""}>
              {record.parentOrganizationId
                ? `${orgNameMap.get(record.parentOrganizationId) ?? record.parentOrganizationId} (${record.parentOrganizationId})`
                : ''}
            </Td>
            <Td borderRight="1px solid" borderColor="gray.200" bgColor={bgColor} {...ellipsisStyle}>{formatYearMonthByLocale(record.fiscalPeriod)}</Td>
            <Td borderRight="1px solid" borderColor="gray.200" bgColor={bgColor} textAlign="center">{record.deleted ? (
              <Tag size="lg" colorScheme='red'>{String(record.deleted)}</Tag>
            ) : (
              <Tag size="lg" colorScheme='green'>{String(record.deleted)}</Tag>
            )
            }</Td>
            <Td borderRight="1px solid" borderColor="gray.200" bgColor={bgColor} {...ellipsisStyle}>{formatLocalDateTime(record.createDate)}</Td>
            <Td borderRight="1px solid" borderColor="gray.200" bgColor={bgColor} {...ellipsisStyle}>{formatLocalDateTime(record.updateDate)}</Td>
          </Tr>
        )
      })}
    </Tbody>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [filteredMasterData, deletedOrgIds, orgNameMap]);

  /**
   * 現在の会計年度を取得（4月始まり）
   */
  const getCurrentFiscalYear = useCallback(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    // 4月以降は現在年、3月以前は前年
    return currentMonth >= 4 ? currentYear : currentYear - 1;
  }, []);

  /**
   * 指定された適用年度が過去かどうかを判定
   * @param fiscalPeriod 適用年度（YYYY-MM形式）
   * @returns true: 過去年度、false: 現在または未来年度
   */
  const isPastFiscalYear = useCallback((fiscalPeriod: string) => {
    if (!fiscalPeriod) return false;
    const year = parseInt(fiscalPeriod.split('-')[0], 10);
    return year < getCurrentFiscalYear();
  }, [getCurrentFiscalYear]);


  /**
   * 管理体系データを取得（共通関数）
   * @param fiscalPeriod 適用年度
   * @param hasDeleted 削除済みを含めるか
   * @returns 取得したデータ（エラー時はnull）
   */
  const fetchOrganizationData = useCallback(async (fiscalPeriod: string, hasDeleted: boolean): Promise<AccountingOrganizationType[] | null> => {
    try {
      const payload = {
        query: `
          query getAccountingOrganization($hasDeleted: Boolean, $fiscalPeriod: String) {
            getAccountingOrganization(hasDeleted: $hasDeleted, fiscalPeriod: $fiscalPeriod) {
              organizationId
              organizationName
              organizationNameEn
              parentOrganizationId
              fiscalPeriod
              deleted
              createDate
              updateDate
              order
            }
          }`,
        variables: {
          hasDeleted: hasDeleted,
          fiscalPeriod: fiscalPeriod,
        }
      };

      const response = await WebAPI.request(payload);

      if (response?.data?.getAccountingOrganization) {
        return response.data.getAccountingOrganization;
      } else {
        console.error(`Failed to call API getAccountingOrganization. response = `, response);
        return null;
      }
    } catch (error) {
      console.error("Failed to fetch organization data", error);
      return null;
    }
  }, [WebAPI]);

  /**
   * オートコンプリート用データを取得（削除済み除外）
   * @param fiscalPeriod 適用年度
   */
  const fetchAutocompleteData = async (fiscalPeriod: string) => {
    const data = await fetchOrganizationData(fiscalPeriod, false);
    if (data) {
      setAutocompleteData(data);
      setAutocompleteFiscalPeriod(fiscalPeriod);
    }
  };

  /**
   * 対象のマスタデータ取得（削除済み含む）
   * @param overrideFiscalPeriod 指定時はこの値で検索（省略時はselectedFiscalPeriodを使用）
   */
  const fetchMasterData = async (overrideFiscalPeriod?: string) => {
    const fiscalPeriod = overrideFiscalPeriod || selectedFiscalPeriod;
    if (!fiscalPeriod) {
      return;
    }

    const data = await fetchOrganizationData(fiscalPeriod, true);
    if (data) {
      setMasterData(data);
    } else {
      toast({
        title: TITLE,
        description: t('common.message.fetchListFailed', { name: ENTITY }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「新規登録」ボタン押下
   */
  const handleOpenEditModal = async () => {
    // オートコンプリート用データの取得（適用年度が指定されている場合のみ）
    if (selectedFiscalPeriod) {
      // 検索結果と適用年度が一致 → masterDataをそのまま使用
      // キャッシュ済みのautocompleteDataと適用年度が一致 → キャッシュを再利用
      // それ以外 → APIから別途取得
      if (hasSearched && searchedFiscalPeriod === selectedFiscalPeriod) {
        setAutocompleteData([]);
        setAutocompleteFiscalPeriod("");
      } else if (autocompleteFiscalPeriod !== selectedFiscalPeriod) {
        await fetchAutocompleteData(selectedFiscalPeriod);
      }
    }

    // 新規登録時は適用年度をセット（未指定の場合は空）
    setEditTarget({
      fiscalPeriod: selectedFiscalPeriod || "",
    });
    setOriginalFiscalPeriod(""); // 新規登録なので空
    setParentOrgInputValue("");
    setShowParentOrgSuggestions(false);
    onOpenEditModal();
  }

  /**
   * 指定したプロパティ名の値を変更
   * @param propertyName プロパティ名
   * @param value 値
   */
  const changeEditTarget = (propertyName: string, value: any) => {
    setEditTarget((prevState) => ({
      ...prevState,
      [propertyName]: value,
    }));
  }

  /**
   * 「組織ID」リンク押下
   * @param organizationId 組織ID
   * @param fiscalPeriod 適用年度
   */
  const handleEdit = async (organizationId?: string | null, fiscalPeriod?: string | null) => {
    try {
      const payload = {
        query: `
          query getAccountingOrganizationById($organizationId: String!, $fiscalPeriod: String!, $exact: Boolean) {
            getAccountingOrganizationById(organizationId: $organizationId, fiscalPeriod: $fiscalPeriod, exact: $exact) {
              organizationId
              organizationName
              organizationNameEn
              parentOrganizationId
              fiscalPeriod
              deleted
              createDate
              updateDate
              order
            }
          }`,
        variables: {
          organizationId: organizationId,
          fiscalPeriod: fiscalPeriod,
          exact: true,
        }
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getAccountingOrganizationById) {
        // 管理体系マスタ情報
        const data = response.data.getAccountingOrganizationById;
        setEditTarget(data);
        setOriginalFiscalPeriod(data.fiscalPeriod || ""); // 編集前の適用年度を保持
        setParentOrgInputValue(data.parentOrganizationId || "");
        setShowParentOrgSuggestions(false);

        // 検索結果と適用年度が一致 → masterDataを使用
        // キャッシュ済みと一致 → キャッシュを再利用
        // それ以外 → APIから別途取得
        if (hasSearched && searchedFiscalPeriod === fiscalPeriod) {
          setAutocompleteData([]);
          setAutocompleteFiscalPeriod("");
        } else if (fiscalPeriod && autocompleteFiscalPeriod !== fiscalPeriod) {
          await fetchAutocompleteData(fiscalPeriod);
        }

        // 編集モーダル表示
        onOpenEditModal();
      } else {
        console.error(`Failed to call API getAccountingOrganizationById. response = `, response);
        toast({
          title: TITLE,
          description: t('common.message.fetchInfoFailed', { name: ENTITY }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. handleEdit", error);
      toast({
        title: TITLE,
        description: t('common.message.fetchInfoFailed', { name: ENTITY }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {
    if (isSaving) return;

    if (!validate()) {
      toast({
        title: TITLE,
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // 過去年度の場合は確認ダイアログを表示
    const targetFiscalPeriod = editTarget.fiscalPeriod || selectedFiscalPeriod;
    if (isPastFiscalYear(targetFiscalPeriod)) {
      onOpenPastYearAlert();
      return;
    }

    // 保存実行
    await executeSave();
  }

  /**
   * 保存処理を実行
   */
  const executeSave = async () => {
    setIsSaving(true);

    // 登録・更新
    if (!await putEditTarget()) {
      setIsSaving(false);
      return;
    }

    // 検索済みの場合のみ一覧更新
    if (hasSearched && searchedFiscalPeriod) {
      await fetchMasterData(searchedFiscalPeriod);
    }

    // モーダル閉じる
    setIsSaving(false);
    onCloseEditModal();
  }

  /**
   * 過去年度編集確認後の保存実行
   */
  const handleConfirmPastYearSave = async () => {
    onClosePastYearAlert();
    await executeSave();
  }

  /**
   * Excel一括登録データのバリデーション
   * 親組織IDの存在チェックと循環参照チェックを実施する。
   * @param data インポートデータ
   * @param existingData 登録先適用年度の既存組織データ（バリデーション用に取得したもの）
   * @returns エラーメッセージ配列（空なら検証OK）
   */
  const validateImportData = (data: AccountingOrganizationType[], existingData: AccountingOrganizationType[]): string[] => {
    const errors: string[] = [];

    // インポートデータの組織ID一覧
    const importOrgIds = new Set(data.map(d => d.organizationId).filter(Boolean));
    // 既存データの組織ID一覧（削除済みは除外）
    const existingOrgIds = new Set(existingData.filter(m => !m.deleted).map(m => m.organizationId).filter(Boolean));
    // 統合した組織ID一覧（既存 + インポート）
    const allOrgIds = new Set([...existingOrgIds, ...importOrgIds]);

    // 親組織IDの存在チェック
    for (const item of data) {
      if (item.parentOrganizationId) {
        if (item.parentOrganizationId === item.organizationId) {
          errors.push(t('features.MasterMaintenance.AccountingOrganization.message.bulkError.selfParent', { organizationId: item.organizationId }));
        } else if (!allOrgIds.has(item.parentOrganizationId)) {
          errors.push(t('features.MasterMaintenance.AccountingOrganization.message.bulkError.parentNotExists', { organizationId: item.organizationId, parentId: item.parentOrganizationId }));
        }
      }
    }
    // 存在チェックでエラーがある場合は循環参照チェックをスキップ
    if (errors.length > 0) {
      return errors;
    }

    // 循環参照チェック
    // 親子マップを構築（既存データの削除済みは除外 + インポートデータで上書き）
    const parentMap = new Map<string, string>();
    for (const item of existingData) {
      if (item.organizationId && item.parentOrganizationId && !item.deleted) {
        parentMap.set(item.organizationId, item.parentOrganizationId);
      }
    }
    for (const item of data) {
      if (item.organizationId && item.parentOrganizationId) {
        parentMap.set(item.organizationId, item.parentOrganizationId);
      } else if (item.organizationId && !item.parentOrganizationId) {
        parentMap.delete(item.organizationId); // 親組織なしで上書き
      }
    }

    // 各ノードから親を辿って循環を検出
    for (const item of data) {
      if (!item.organizationId || !item.parentOrganizationId) continue;

      const visited = new Set<string>();
      let current: string | undefined = item.parentOrganizationId;
      while (current) {
        if (current === item.organizationId) {
          errors.push(t('features.MasterMaintenance.AccountingOrganization.message.bulkError.circular', { organizationId: item.organizationId }));
          break;
        }
        if (visited.has(current)) break;
        visited.add(current);
        current = parentMap.get(current);
      }
    }

    return errors;
  };

  /**
   * 循環参照チェック（単一レコード用）
   * 現在のソースデータ上の親子関係を辿り、循環が発生するか検出する。
   * @param organizationId 対象の組織ID
   * @param parentOrganizationId 設定しようとしている親組織ID
   * @returns true: 循環参照あり
   */
  const hasCircularReference = (organizationId: string, parentOrganizationId: string): boolean => {
    // ソースデータから親子マップを構築（削除済みは除外、現在の編集対象を反映）
    const sourceData = autocompleteData.length > 0 ? autocompleteData : masterData;
    const parentMap = new Map<string, string>();
    for (const item of sourceData) {
      if (item.organizationId && item.parentOrganizationId && !item.deleted) {
        parentMap.set(item.organizationId, item.parentOrganizationId);
      }
    }
    // 編集対象の親組織IDを上書き
    parentMap.set(organizationId, parentOrganizationId);

    // 親を辿って循環を検出
    const visited = new Set<string>();
    let current: string | undefined = parentOrganizationId;
    while (current) {
      if (current === organizationId) {
        return true; // 循環検出
      }
      if (visited.has(current)) {
        break; // 既に訪問済み（別の循環、対象とは無関係）
      }
      visited.add(current);
      current = parentMap.get(current);
    }
    return false;
  };

  /**
   * バリデーションチェックを行う。
   * @returns true：検証成功、false：検証失敗
   */
  const validate = () => {
    setErrors({});

    const localErrors: Errors = {};

    // 必須項目チェック
    const required = (value?: string | number | null) => Boolean(value || value === 0);

    // 必須項目チェックとエラー情報設定
    const requiredAndSetError = (target: any, propertyName: string, propertyNameJp: string) => {
      const requiredResult = required(target[propertyName]);

      if (!requiredResult) {
        localErrors[propertyName] = {
          isError: true,
          errorMessage: t('common.message.requiredField', { fieldName: propertyNameJp }),
        };
      }
      return requiredResult;
    }

    let result = true;

    if (!requiredAndSetError(editTarget, "organizationId", t('features.MasterMaintenance.AccountingOrganization.field.organizationId'))) {
      result = false;
    }
    if (!requiredAndSetError(editTarget, "fiscalPeriod", t('features.MasterMaintenance.AccountingOrganization.field.fiscalPeriod'))) {
      result = false;
    }
    if (!requiredAndSetError(editTarget, "organizationName", t('features.MasterMaintenance.AccountingOrganization.field.organizationName'))) {
      result = false;
    }
    if (!requiredAndSetError(editTarget, "organizationNameEn", t('features.MasterMaintenance.AccountingOrganization.field.organizationNameEn'))) {
      result = false;
    }

    // 親組織IDのバリデーション（入力がある場合のみ）
    if (editTarget.parentOrganizationId) {
      // 自分自身を親に設定していないかチェック
      if (editTarget.parentOrganizationId === editTarget.organizationId) {
        localErrors["parentOrganizationId"] = {
          isError: true,
          errorMessage: t('features.MasterMaintenance.AccountingOrganization.message.cannotSelfParent'),
        };
        result = false;
      } else {
        // 親組織IDが存在するかチェック（ソースデータ内、削除済みは除外）
        const sourceData = autocompleteData.length > 0 ? autocompleteData : masterData;
        const existsInMaster = sourceData.some(
          m => m.organizationId === editTarget.parentOrganizationId && !m.deleted
        );
        if (!existsInMaster) {
          localErrors["parentOrganizationId"] = {
            isError: true,
            errorMessage: t('features.MasterMaintenance.AccountingOrganization.message.parentNotExists', { parentId: editTarget.parentOrganizationId }),
          };
          result = false;
        } else {
          // 循環参照チェック
          if (hasCircularReference(editTarget.organizationId!, editTarget.parentOrganizationId)) {
            localErrors["parentOrganizationId"] = {
              isError: true,
              errorMessage: t('features.MasterMaintenance.AccountingOrganization.message.circularReference'),
            };
            result = false;
          }
        }
      }
    }

    setErrors(localErrors);
    return result;
  }

  /**
   * 入力したデータを登録・更新
   * 適用年度が変更された場合はupdateDateをnullにしてPUT（新規登録）として処理
   */
  const putEditTarget = async () => {
    try {
      // 適用年度が変更された場合はPUT（新規登録）
      const isFiscalPeriodChanged = originalFiscalPeriod && editTarget.fiscalPeriod !== originalFiscalPeriod;

      // order採番: 更新時は既存値を維持、新規時は現在の最大値+1
      const nextOrder = (editTarget.updateDate && !isFiscalPeriodChanged)
        ? editTarget.order ?? null
        : Math.max(0, ...masterData.map(o => o.order ?? 0)) + 1;

      // 登録パラメータ
      const input = {
        organizationId: editTarget.organizationId,
        fiscalPeriod: editTarget.fiscalPeriod,
        organizationName: editTarget.organizationName,
        organizationNameEn: editTarget.organizationNameEn || null,
        parentOrganizationId: editTarget.parentOrganizationId || null,
        deleted: editTarget.deleted || false,
        updateDate: isFiscalPeriodChanged ? null : editTarget.updateDate,
        order: nextOrder,
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
          mutation putAccountingOrganization($input: AccountingOrganizationInput!) {
            putAccountingOrganization(input: $input) {status}
          }
        `,
        variables: {
          input: input,
        }
      });

      if (result.data.putAccountingOrganization.status === "success") {
        toast({
          title: TITLE,
          description: t('common.message.inputSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        console.log(`Success to call API putAccountingOrganization.`);
        return true;
      } else if (result?.data?.putAccountingOrganization.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putAccountingOrganization.`);
        return false;
      } else {
        toast({
          title: TITLE,
          description: t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putAccountingOrganization. response`, result);
        return false;
      }
    } catch (error) {
      console.error("Exception to call API putAccountingOrganization. error = ", error);
      toast({
        title: TITLE,
        description: t('common.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  }

  /**
   * 「閉じる」ボタン押下
   */
  const handleClose = async () => {
    // 検索済みの場合のみ一覧更新（検索前はデータ取得不要）
    if (hasSearched && searchedFiscalPeriod) {
      await fetchMasterData(searchedFiscalPeriod);
    }
    onCloseEditModal();
  }

  /**
   * 編集モーダル閉じる時、初期化する
   * ※ autocompleteData/autocompleteFiscalPeriod はキャッシュとして保持（クリアしない）
   */
  const handleCloseComplete = () => {
    // 初期化
    setEditTarget({});
    setOriginalFiscalPeriod("");
    setErrors({});
  }

  /**
   * Excelアップロードボタン押下
   */
  const handleExcelUploadClick = () => {
    fileInputRef.current?.click();
  }

  /**
   * Excelファイル読み込み処理
   */
  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      WebAPI.setLoading(true);

      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          if (!evt?.target?.result) return;

          const data = new Uint8Array(evt.target.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });

          // 最初のシートを取得
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // シートを配列として読み込み（ヘッダー行を探すため）
          const rawData = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });

          let importedData: AccountingOrganizationType[] = [];

          // 階層形式の場合は先に既存データを取得して仮IDの衝突回避と自動紐づけに共用する
          let preloadedExistingData: AccountingOrganizationExistType[] | null = null;
          const detectedFormat = detectExcelFormat(rawData);
          setIsHierarchyFormat(detectedFormat === 'hierarchy');
          if (detectedFormat === 'hierarchy') {
            if (selectedFiscalPeriod) {
              preloadedExistingData = await fetchOrganizationData(selectedFiscalPeriod, true) as AccountingOrganizationExistType[] | null;
            }
            const existingIds = new Set((preloadedExistingData ?? []).map(d => d.organizationId ?? '').filter(Boolean));
            importedData = loadHierarchyFormatExcel(rawData, existingIds);
          } else {
            importedData = loadFlatFormatExcel(rawData);
          }

          // 件数上限チェック（バックエンドと同じ上限）
          if (importedData.length > MAX_IMPORT_SIZE) {
            toast({
              title: TITLE,
              description: t('features.MasterMaintenance.AccountingOrganization.message.bulkImportLimitExceeded', { max: MAX_IMPORT_SIZE, count: importedData.length }),
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            WebAPI.setLoading(false);
            return null;
          }

          // 必須項目バリデーション
          const validData = importedData.filter(item => item.organizationId && item.organizationName && item.organizationNameEn);
          if (validData.length === 0) {
            toast({
              title: TITLE,
              description: t('features.MasterMaintenance.AccountingOrganization.message.excelNoData'),
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            WebAPI.setLoading(false);
            return null;
          }

          setBatchFiscalPeriod(selectedFiscalPeriod);

          // 親画面で適用年度が指定済みの場合、モーダルを開く前に既存データを取得して自動紐づけを実行
          // 階層形式では上で取得済みの preloadedExistingData を再利用し、API二重呼び出しを防ぐ
          if (selectedFiscalPeriod) {
            const fetchedExistingData = preloadedExistingData
              ?? await fetchOrganizationData(selectedFiscalPeriod, true) as AccountingOrganizationExistType[] | null;
            if (fetchedExistingData && fetchedExistingData.length > 0) {
              // 既存データあり → 自動マッチング実行
              const mapped = mappingOrganizationData(validData, fetchedExistingData);
              setImportData(mapped);
              setExistingData(fetchedExistingData);
              // インポートデータに紐づかなかった既存組織を無効化候補にセット
              const usedIds = new Set(mapped.map(r => r.mappingOrganizationId).filter((id): id is string => !!id));
              const candidates = fetchedExistingData.filter(d => !d.deleted && !usedIds.has(d.organizationId ?? ''));
              const candidateIds = new Set(candidates.map(c => c.organizationId ?? ''));
              setInvalidateData(candidates.map(c => ({ ...c, excluded: false })));
              setOriginalInvalidateIds(candidateIds);
            } else {
              // 既存データなし（0件またはAPIエラー）→ すべて新規扱い
              setImportData(validData.map(item => ({ ...item, status: "new" as const })));
              setExistingData([]);
              setInvalidateData([]);
              setOriginalInvalidateIds(new Set());
            }
          } else {
            // 適用年度未指定 → unmapped で初期化してプレビューを表示
            setImportData(validData.map(item => ({ ...item, status: "unmapped" as const })));
            setInvalidateData([]);
            setOriginalInvalidateIds(new Set());
          }

          onOpenPreviewModal();

        } catch (err) {
          toast({
            title: t('features.MasterMaintenance.AccountingOrganization.errorTitle'),
            description: t('features.MasterMaintenance.AccountingOrganization.message.excelReadFailed'),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          console.error('Excel read error:', err);
        } finally {
          WebAPI.setLoading(false);
        }
      };
      e.target.value = '';
      reader.readAsArrayBuffer(file);
    },
    [selectedFiscalPeriod, WebAPI, toast, onOpenPreviewModal, t, TITLE, fetchOrganizationData]
  );

  /**
   * ExcelのrawDataからフォーマットを自動判定する。
   * HIERARCHY_COLUMN_DEFS のパターンにヒットすれば階層形式、それ以外は現行形式と判断する。
   * @param rawData sheet_to_json で取得した生データ
   * @returns 'hierarchy': 階層形式（新形式）、'flat': 現行形式（組織ID付き）
   */
  const detectExcelFormat = (rawData: any[][]): 'hierarchy' | 'flat' => {
    const hierarchyPatterns = new Set(HIERARCHY_COLUMN_DEFS.flatMap(c => c.patterns));
    for (const row of rawData) {
      if (row && row.some((cell: any) => cell && hierarchyPatterns.has(String(cell).trim()))) {
        return 'hierarchy';
      }
    }
    return 'flat';
  };

  /**
   * ヘッダー行インデックスを探して返す
   * @param columnDefs カラム情報
   * @param rawData 生データ
   * @returns ヘッダー行インデックス
   */
  const getHeaderRowIndex = (columnDefs: { key: string, patterns: string[] }[], rawData: any[][]) => {
    // ヘッダー検出用：全カラムの全パターンを集約
    const allHeaderPatterns = new Set(columnDefs.flatMap(c => c.patterns));

    // ヘッダー行を探す（定義済みヘッダー名に完全一致するセルを含む行）
    let headerRowIndex = 0;
    for (let i = 0; i < rawData.length; i++) {
      const row = rawData[i] as any[];
      if (row && row.some(cell => cell && allHeaderPatterns.has(String(cell).trim()))) {
        headerRowIndex = i;
        break;
      }
    }

    return headerRowIndex;
  }

  /**
   * 生データをオブジェクト配列に変換
   * @param dataRows 生データ
   * @param headers ヘッダー部
   * @param columnDefs カラム定義
   * @param useTrim トリム使用フラグ(true：文字列化後トリムする、false：トリムしない)
   * @returns オブジェクト配列
   */
  const rawDataToObjectArray = (dataRows: any[][], headers: string[], columnDefs?: { key: string, patterns: string[] }[], useTrim: boolean = false) => {
    // データをオブジェクト形式に変換
    const result = dataRows
      .filter(row => row && (row as any[]).length > 0)  // 空行を除外
      .map(row => {
        const obj: any = {};
        headers.forEach((header, index) => {
          if (header) {
            let propName = columnDefs?.find(colDef => colDef.patterns.some(p => p === header))?.key;
            propName = propName ?? header;

            const value = (row as any[])[index];

            obj[String(propName).trim()] = (useTrim ? String(value ?? '').trim() : value);
          }
        });
        return obj;
      });

    return result;
  }

  /**
   * 現行形式（組織ID付きフラット）のExcelを読み込んで管理体系データを返す。
   * @param rawData sheet_to_json で取得した生データ
   * @returns 管理体系データ
   */
  const loadFlatFormatExcel = (rawData: any[][]) => {

    const columnDefs = FLAT_COLUMN_DEFS;

    // ヘッダー行インデックス取得
    const headerRowIndex: number = getHeaderRowIndex(columnDefs, rawData);

    // ヘッダー行とデータ行を取得
    const headers = rawData[headerRowIndex] as string[];
    const dataRows = rawData.slice(headerRowIndex + 1);

    // データをオブジェクト形式に変換
    const jsonData = rawDataToObjectArray(dataRows, headers);

    // データをAccountingOrganizationType形式に変換
    const getColumnValue = (row: any, patterns: string[]): string | undefined => {
      // まず完全一致を試行
      for (const pattern of patterns) {
        if (row[pattern] !== undefined) return row[pattern];
      }
      // 次に部分一致を試行（キーがパターンを含む場合のみ）
      for (const pattern of patterns) {
        const key = Object.keys(row).find(k => k.includes(pattern));
        if (key && row[key] !== undefined) return row[key];
      }
      return undefined;
    };

    // 管理体系マスタ型配列に変換
    const importedData: AccountingOrganizationType[] = jsonData.map((row: any) => {
      return {
        status: "unmapped",
        organizationId: String(getColumnValue(row, columnDefs[0].patterns) || ''),
        organizationName: String(getColumnValue(row, columnDefs[1].patterns) || ''),
        organizationNameEn: String(getColumnValue(row, columnDefs[2].patterns) || ''),
        parentOrganizationId: String(getColumnValue(row, columnDefs[3].patterns) || ''),
        deleted: false,
      };
    });

    return importedData;
  }

  /**
   * 階層形式（新形式・6階層横並び）のExcelを読み込んで管理体系データを返す。
   * 仮IDは organization00000 形式の連番で採番するが、existingIds に含まれるIDはスキップして衝突を防ぐ。
   * @param rawData sheet_to_json で取得した生データ
   * @param existingIds 既存データの組織IDセット（仮ID採番時の衝突回避に使用）
   * @returns 管理体系データ
   */
  const loadHierarchyFormatExcel = (rawData: any[][], existingIds: Set<string> = new Set()) => {
    const columnDefs = HIERARCHY_COLUMN_DEFS;

    // ヘッダー行インデックス取得
    const headerRowIndex: number = getHeaderRowIndex(columnDefs, rawData);

    // ヘッダー行とデータ行を取得
    const headers = rawData[headerRowIndex] as string[];
    const dataRows = rawData.slice(headerRowIndex + 1);

    // データをオブジェクト形式に変換
    const objArray = rawDataToObjectArray(dataRows, headers, columnDefs, true);

    // ノードマップ key : "{level}:{jaName}:{parentId}"、value : organizationId
    const nodeMap: Map<string, string> = new Map();
    const importedData: ImportDataType[] = [];
    let counter = 0;

    // 組織ID取得または生成
    const getOrCreateId = (level: number, jaName: string, enName: string, parentId: string | null | undefined) => {
      const key = `${level}:${jaName}:${enName ?? ''}:${parentId ?? ''}`;
      if (nodeMap.has(key)) {
        return nodeMap.get(key);
      }
      // 組織ID採番：既存IDと衝突しないようスキップしながら採番
      let id: string;
      do {
        id = `organization${String(counter).padStart(5, '0')}`;
        counter++;
      } while (existingIds.has(id));
      nodeMap.set(key, id);
      importedData.push({
        status: "unmapped",
        organizationId: id,
        organizationName: jaName,
        organizationNameEn: enName,
        parentOrganizationId: parentId ?? '',
      });
      return id;
    }

    // 最上位: ミネベアミツミ（CSVと同じくrootとして追加）
    const rootId = getOrCreateId(0, 'ミネベアミツミ', 'MinebeaMitsumi', null);

    for (const rowObj of objArray) {
      if (!rowObj.businessHeadquartersJa) {
        continue;
      }

      const bhqId = getOrCreateId(1, rowObj.businessHeadquartersJa, rowObj.businessHeadquartersEn, rootId);
      const divId = getOrCreateId(2, rowObj.departmentJa, rowObj.departmentEn, bhqId);
      const buId = getOrCreateId(3, rowObj.businessUnitJa, rowObj.businessUnitEn, divId);
      const stId = getOrCreateId(4, rowObj.businessSubtotalJa, rowObj.businessSubtotalEn, buId);
      const bizId = getOrCreateId(5, rowObj.businessJa, rowObj.businessEn, stId);
      getOrCreateId(6, rowObj.productJa, rowObj.productEn, bizId);
    }

    return importedData;
  }

  /**
   * Excelデータと既存データの自動紐づけ。
   * 組織名（日本語または英語）が一致する既存組織を、組織IDの昇順で優先して1対1に紐づける。
   * 既に他の行に紐づけ済みの組織は候補から除外する。
   * @param currentImportData 取り込みデータ
   * @param existingData 既存データ
   * @returns 紐づけ結果を反映した新しい取り込みデータ配列
   */
  const mappingOrganizationData = (
    currentImportData: ImportDataType[],
    existingData: AccountingOrganizationExistType[]
  ): ImportDataType[] => {
    // 組織IDの昇順にソートしたコピーを使用（元配列を変更しない）
    const sortedExisting = [...existingData].sort((a, b) =>
      (a.organizationId ?? '').localeCompare(b.organizationId ?? '')
    );
    // 処理中に紐づけ済みになった組織IDを追跡するセット
    const usedIds = new Set<string>();

    const mapped = currentImportData.map(importRow => {
      const matched = sortedExisting.find(row =>
        !row.deleted &&
        !usedIds.has(row.organizationId ?? '') &&
        (row.organizationName === importRow.organizationName || row.organizationNameEn === importRow.organizationNameEn)
      );

      if (matched?.organizationId) {
        usedIds.add(matched.organizationId);
        return {
          ...importRow,
          status: "overwrite" as const,
          mappingOrganizationId: matched.organizationId,
          mappingOrganizationName: matched.organizationName,
        };
      }

      return { ...importRow, status: "unmapped" as const };
    });

    // 既存データがすべて紐づけ済みの場合、unmappedのままの行は新規として扱う
    // 削除済みは紐づけ対象外のため、非削除件数のみで判定する
    const allExistingUsed = usedIds.size >= existingData.filter(d => !d.deleted).length;
    if (!allExistingUsed) return mapped;

    return mapped.map(row =>
      row.status === "unmapped"
        ? { ...row, status: "new" as const }
        : row
    );
  }

  /**
   * 紐づけモーダルを開く
   * @param index 操作対象の importData インデックス
   */
  const handleOpenMappingModal = (index: number) => {
    setMappingTargetIndex(index);
    // モーダルを開く時点で現在の紐づけIDを初期選択にする（未設定なら「該当なし」=""）
    setMappingSelectedId(importData[index]?.mappingOrganizationId ?? "");
    onOpenMappingModal();
  };

  /**
   * 紐づけモーダルで「確定」を押した時の処理。
   * importData を更新し、invalidateData との連動更新も行う。
   * - 新たに選んだ組織IDが invalidateData にある → invalidateData から除外
   * - 変更前の紐づけIDが空でなく新IDと異なり、他の行で使用されていない → invalidateData に追加
   */
  const handleConfirmMapping = () => {
    if (mappingTargetIndex < 0) return;
    const selectedExisting = existingData.find(d => d.organizationId === mappingSelectedId);
    // 変更前の紐づけIDを取得（連動更新の判定に使用）
    const prevMappingId = importData[mappingTargetIndex]?.mappingOrganizationId ?? "";

    setImportData(prev => prev.map((row, i) => {
      if (i !== mappingTargetIndex) return row;
      if (!mappingSelectedId) {
        // 「該当なし」を選択 → 新規登録扱い
        return { ...row, status: "new" as const, mappingOrganizationId: undefined, mappingOrganizationName: undefined };
      }
      return {
        ...row,
        status: "overwrite" as const,
        mappingOrganizationId: selectedExisting?.organizationId,
        mappingOrganizationName: selectedExisting?.organizationName,
      };
    }));

    // invalidateData との連動更新
    setInvalidateData(prev => {
      let updated = [...prev];
      // ルール1: 新たに選んだ組織IDが invalidateData にある → 除外（削除）
      if (mappingSelectedId) {
        updated = updated.filter(d => d.organizationId !== mappingSelectedId);
      }
      // ルール2: 変更前の紐づけIDが空でなく、新IDと異なる場合
      //   他の行で使われていなければ無効化候補に追加（originalInvalidateIds の有無に関わらず対象とする）
      if (prevMappingId && prevMappingId !== mappingSelectedId) {
        const usedInOtherRows = importData.some(
          (row, i) => i !== mappingTargetIndex && row.mappingOrganizationId === prevMappingId
        );
        const alreadyExists = updated.some(d => d.organizationId === prevMappingId);
        if (!alreadyExists && !usedInOtherRows) {
          const original = existingData.find(d => d.organizationId === prevMappingId);
          if (original) {
            updated = [...updated, { ...original, excluded: false }];
          }
        }
      }
      return updated;
    });

    onCloseMappingModal();
  };

  /**
   * プレビュー表示モーダルを閉じてステートをリセットする。
   * キャンセルボタン・× ボタン・オーバーレイクリック時に使用する。
   * 一括登録成功後は batchFiscalPeriod を参照する処理が後続するため、finally ブロックで個別リセットする。
   */
  const handleClosePreviewModal = () => {
    onClosePreviewModal();
    setBatchFiscalPeriod("");
    setImportData([]);
    setExistingData([]);
    setInvalidateData([]);
    setOriginalInvalidateIds(new Set());
    setIsHierarchyFormat(false);
  };

  /**
   * Excel一括登録実行
   */
  const handleBatchImport = async () => {
    // 適用年度の未入力チェック
    if (!batchFiscalPeriod) {
      toast({
        title: TITLE,
        description: t('features.MasterMaintenance.AccountingOrganization.message.fiscalYearRequired'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (importData.length === 0) {
      return;
    }

    try {
      WebAPI.setLoading(true);

      // T7: ローカル変数名を fetchedExistingData にリネーム（State の existingData との混在を解消）
      const fetchedExistingData = await fetchOrganizationData(batchFiscalPeriod, true);
      if (!fetchedExistingData) {
        // 既存データ取得失敗（APIエラー）→ バリデーション不能のため登録を中断
        toast({
          title: TITLE,
          description: t('common.message.fetchFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        WebAPI.setLoading(false);
        return;
      }

      const importErrors = validateImportData(importData, fetchedExistingData);
      if (importErrors.length > 0) {
        toast({
          title: TITLE,
          description: (
            <VStack align="start" spacing={0}>
              {importErrors.map((err, i) => (
                <Text key={i}>{err}</Text>
              ))}
            </VStack>
          ),
          status: 'error',
          duration: 10000,
          isClosable: true,
        });
        WebAPI.setLoading(false);
        return;
      }

      // 仮IDから実際の組織IDへの変換マップを作成
      // 階層形式Excelでは organizationId が仮IDのため、mappingOrganizationId（既存ID）または新規IDに変換する

      // new行の仮IDを、既存IDの最大値+1から順に採番し直す
      // （仮IDが既存IDと衝突する場合があるため、登録直前に確定した実IDへ変換する）
      const existingNumericIds = (fetchedExistingData ?? [])
        .map(d => d.organizationId ?? '')
        .map(id => {
          const match = id.match(/^organization(\d+)$/);
          return match ? parseInt(match[1], 10) : -1;
        })
        .filter(n => n >= 0);
      const maxExistingNum = existingNumericIds.length > 0 ? Math.max(...existingNumericIds) : -1;
      let newIdCounter = maxExistingNum + 1;

      const idMap = new Map<string, string>(
        importData
          .filter(item => item.organizationId && (item.mappingOrganizationId || item.status === "new"))
          .map(item => {
            if (item.status === "overwrite") {
              // overwrite行は既存の実IDを使用
              return [item.organizationId!, item.mappingOrganizationId!];
            } else {
              // new行は既存IDの最大値+1から順に実IDを採番
              const newId = `organization${String(newIdCounter).padStart(5, '0')}`;
              newIdCounter++;
              return [item.organizationId!, newId];
            }
          })
      );

      // T6: 無効化候補（excluded !== true）を deleted:true で送信リストに追加
      const invalidateInput = invalidateData
        .filter(item => item.excluded !== true)
        .map(item => ({
          organizationId: item.organizationId,
          fiscalPeriod: batchFiscalPeriod,
          organizationName: item.organizationName,
          organizationNameEn: item.organizationNameEn || null,
          parentOrganizationId: item.parentOrganizationId || null,
          deleted: true,
          order: item.order ?? null,
        }));

      // 一括登録API呼び出し（適用年度はダイアログで指定された値を使用）
      const result = await WebAPI.request({
        query: `
          mutation putAccountingOrganizationBatch($input: [AccountingOrganizationInput]!) {
            putAccountingOrganizationBatch(input: $input) {status}
          }
        `,
        variables: {
          input: [
            // インポートデータ（上書き・新規）
            ...importData.map((item, index) => ({
              // overwrite行は実ID、new行は採番した実ID（idMap経由）を使用
              organizationId: idMap.get(item.organizationId!) ?? item.organizationId,
              fiscalPeriod: batchFiscalPeriod,
              organizationName: item.organizationName,
              organizationNameEn: item.organizationNameEn || null,
              // parentOrganizationId も仮IDから実IDへ変換（空文字列は null として送信）
              parentOrganizationId: idMap.get(item.parentOrganizationId ?? '') || item.parentOrganizationId || null,
              deleted: item.deleted,
              order: index + 1,
            })),
            // 無効化候補（deleted: true）
            ...invalidateInput,
          ],
        }
      });

      if (result.data.putAccountingOrganizationBatch.status === "success") {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.AccountingOrganization.message.bulkRegistered', { count: importData.length }),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // プレビュー表示モーダルを閉じる
        onClosePreviewModal();
        setIsHierarchyFormat(false);
        // 成功時のみインポートデータをクリア（エラー時は再試行できるよう残す）
        setImportData([]);
        setInvalidateData([]);
        setOriginalInvalidateIds(new Set());
        setBatchFiscalPeriod("");

        // 一括登録した適用年度で検索結果を更新
        if (batchFiscalPeriod) {
          setSelectedFiscalPeriod(batchFiscalPeriod);
          setHasSearched(true);
          setSearchedFiscalPeriod(batchFiscalPeriod);
          await fetchMasterData(batchFiscalPeriod);
        }
      } else {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.AccountingOrganization.message.bulkRegisterFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putAccountingOrganizationBatch.response`, result);
      }

    } catch (error) {
      console.error("Exception to call API putAccountingOrganizationBatch. error = ", error);
      toast({
        title: TITLE,
        description: t('features.MasterMaintenance.AccountingOrganization.message.bulkRegisterFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      WebAPI.setLoading(false);
    }
  }

  /**
   * 検索ボタン押下
   */
  const handleSearch = () => {
    if (!selectedFiscalPeriod) {
      toast({
        title: TITLE,
        description: t('features.MasterMaintenance.AccountingOrganization.message.fiscalYearInputRequired'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setHasSearched(true);
    setSearchedFiscalPeriod(selectedFiscalPeriod);
    // 検索実行でmasterDataが最新になるため、autocompleteキャッシュをクリア
    setAutocompleteData([]);
    setAutocompleteFiscalPeriod("");
    fetchMasterData(selectedFiscalPeriod);
  };

  /**
   * レイアウト
   */
  return (
    <>
      <Box h="100%" mt="16px">
        <Flex gap={4} align="center">
          <HStack spacing={1}>
            <HStack spacing={1}>
              {/* 適用年度 */}
              <Text fontWeight="bold">{t('features.MasterMaintenance.AccountingOrganization.label.fiscalYearColon')}</Text>
              {/* 適用年度：？アイコン */}
              <HelpIcon placement="bottom" width="600px">
                <Text fontWeight="bold" mb={2}>{t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearSearch.title')}</Text>
                <Text fontSize="sm" mb={2}>
                  {t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearSearch.description')}<Text as="span" fontWeight="bold">{t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearSearch.descriptionBold')}</Text>{t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearSearch.descriptionRest')}
                </Text>
                <Text fontWeight="bold" mb={1} fontSize="sm">{t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearSearch.example')}</Text>

                <Table size="sm" variant="simple" mb={2}>
                  <Thead>
                    <Tr>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.organizationId')}</Th>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.organizationName')}</Th>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.fiscalPeriod')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>ORG-01</Td>
                      <Td>{t('features.MasterMaintenance.AccountingOrganization.exampleTable.organizationA1')}</Td>
                      <Td>{formatYearMonthByLocale('2025-04')}</Td>
                    </Tr>
                    <Tr>
                      <Td>ORG-01</Td>
                      <Td>{t('features.MasterMaintenance.AccountingOrganization.exampleTable.organizationA2')}</Td>
                      <Td>{formatYearMonthByLocale('2025-12')}</Td>
                    </Tr>
                    <Tr>
                      <Td>ORG-02</Td>
                      <Td>{t('features.MasterMaintenance.AccountingOrganization.exampleTable.organizationB')}</Td>
                      <Td>{formatYearMonthByLocale('2025-10')}</Td>
                    </Tr>
                    <Tr>
                      <Td>ORG-03</Td>
                      <Td>{t('features.MasterMaintenance.AccountingOrganization.exampleTable.organizationC')}</Td>
                      <Td>{formatYearMonthByLocale('2025-12')}</Td>
                    </Tr>
                  </Tbody>
                </Table>
                <Text fontSize="sm" mb={1}>
                  {t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearSearch.april2025')}
                </Text>
                <Table size="sm" variant="simple" mb={2}>
                  <Thead>
                    <Tr>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.organizationId')}</Th>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.organizationName')}</Th>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.fiscalPeriod')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>ORG-01</Td>
                      <Td>{t('features.MasterMaintenance.AccountingOrganization.exampleTable.organizationA1')}</Td>
                      <Td>{formatYearMonthByLocale('2025-04')}</Td>
                    </Tr>
                  </Tbody>
                </Table>
                <Text fontSize="sm" mb={1}>
                  {t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearSearch.november2025')}
                </Text>
                <Table size="sm" variant="simple" mb={2}>
                  <Thead>
                    <Tr>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.organizationId')}</Th>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.organizationName')}</Th>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.fiscalPeriod')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>ORG-01</Td>
                      <Td>{t('features.MasterMaintenance.AccountingOrganization.exampleTable.organizationA1')}</Td>
                      <Td>{formatYearMonthByLocale('2025-04')}</Td>
                    </Tr>
                    <Tr>
                      <Td>ORG-02</Td>
                      <Td>{t('features.MasterMaintenance.AccountingOrganization.exampleTable.organizationB')}</Td>
                      <Td>{formatYearMonthByLocale('2025-10')}</Td>
                    </Tr>
                  </Tbody>
                </Table>
                <Text fontSize="sm" mb={1}>
                  {t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearSearch.january2026')}
                </Text>
                <Table size="sm" variant="simple" mb={2}>
                  <Thead>
                    <Tr>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.organizationId')}</Th>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.organizationName')}</Th>
                      <Th>{t('features.MasterMaintenance.AccountingOrganization.field.fiscalPeriod')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td>ORG-01</Td>
                      <Td>{t('features.MasterMaintenance.AccountingOrganization.exampleTable.organizationA2')}</Td>
                      <Td>{formatYearMonthByLocale('2025-12')}</Td>
                    </Tr>
                    <Tr>
                      <Td>ORG-02</Td>
                      <Td>{t('features.MasterMaintenance.AccountingOrganization.exampleTable.organizationB')}</Td>
                      <Td>{formatYearMonthByLocale('2025-10')}</Td>
                    </Tr>
                    <Tr>
                      <Td>ORG-03</Td>
                      <Td>{t('features.MasterMaintenance.AccountingOrganization.exampleTable.organizationC')}</Td>
                      <Td>{formatYearMonthByLocale('2025-12')}</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </HelpIcon>
            </HStack>

            {/* 適用年度（Input） */}
            <Input
              type="month"
              value={selectedFiscalPeriod}
              onChange={(e) => setSelectedFiscalPeriod(e.target.value)}
              width="180px"
            />

            {/* 検索 */}
            <Button colorScheme="blue" onClick={handleSearch} ml={2}>
              {t('common.button.search')}
            </Button>
          </HStack>
          {/* 新規登録 */}
          <Button colorScheme='blue' onClick={(e) => {
            e.currentTarget.blur();
            handleOpenEditModal()
          }}>{t('common.button.newRegistration')}</Button>
          <HStack spacing={1}>
            {/* Excelアップロード */}
            <Button colorScheme='green' onClick={(e) => {
              e.currentTarget.blur();
              handleExcelUploadClick();
            }}>{t('features.MasterMaintenance.InterestRateMaster.excel.uploadButton')}</Button>
            {/* Excelアップロード：？アイコン */}
            <HelpIcon placement="bottom" width="520px">
              <Text fontWeight="bold" mb={2}>{t('features.MasterMaintenance.AccountingOrganization.help.excel.title')}</Text>
              <Text mb={3} fontSize="sm">
                {t('features.MasterMaintenance.AccountingOrganization.help.excel.description')}
              </Text>
              {/* 階層形式 */}
              <Text fontWeight="bold" mb={1} fontSize="sm">{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyFormat')}</Text>
              <Table size="sm" variant="simple" mb={2}>
                <Thead>
                  <Tr>
                    <Th>{t('features.MasterMaintenance.AccountingOrganization.help.excel.columnName')}</Th>
                    <Th>{t('features.MasterMaintenance.AccountingOrganization.help.excel.columnDescription')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyColHq')}</Td>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyLevel1')}</Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyColDivision')}</Td>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyLevel2')}</Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyColBu')}</Td>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyLevel3')}</Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyColSubtotal')}</Td>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyLevel4')}</Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyColBusiness')}</Td>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyLevel5')}</Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyColProduct')}</Td>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.hierarchyLevel6')}</Td>
                  </Tr>
                </Tbody>
              </Table>
              {/* フラット形式（組織ID付き） */}
              <Text fontWeight="bold" mt={3} mb={1} fontSize="sm">{t('features.MasterMaintenance.AccountingOrganization.help.excel.flatFormat')}</Text>
              <Table size="sm" variant="simple" mb={2}>
                <Thead>
                  <Tr>
                    <Th>{t('features.MasterMaintenance.AccountingOrganization.help.excel.columnName')}</Th>
                    <Th>{t('features.MasterMaintenance.AccountingOrganization.help.excel.columnDescription')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.field.organizationId')}</Td>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.idDesc')}</Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.field.organizationName')}</Td>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.nameDesc')}</Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.nameEnHeader')}</Td>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.nameEnDesc')}</Td>
                  </Tr>
                  <Tr>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.field.parentOrganizationId')}</Td>
                    <Td>{t('features.MasterMaintenance.AccountingOrganization.help.excel.parentDesc')}</Td>
                  </Tr>
                </Tbody>
              </Table>
              <Text mt={1} fontSize="sm" color="gray.600">{t('features.MasterMaintenance.AccountingOrganization.help.excel.noteOverwrite')}</Text>
              <Text mt={2} fontSize="sm" color="gray.600">{t('features.MasterMaintenance.AccountingOrganization.help.excel.noteFiscalPeriod')}<br />{t('features.MasterMaintenance.AccountingOrganization.help.excel.noteFiscalPeriod2')}</Text>
            </HelpIcon>
          </HStack>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
          />
        </Flex>

        {/* 検索結果一覧 */}
        <TableContainer overflowX="auto" mt={3} overflowY="auto" h="calc(100dvh - 170px)">
          <Table border="1px solid" borderColor="gray.200" variant="simple" size="sm"
            sx={{ tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0, }}
            width={colWidths.length > 0 ? `${colWidths.reduce((a, b) => a + b, 0) - 15}px` : "calc(100% - 5px)"}
          >
            <Thead position="sticky" top={0} zIndex={2} sx={{ 'th input': { ml: '-2px' } }}>
              <Tr bgColor="gray.50">
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left"
                  width={colWidths[0] ? `${colWidths[0]}px` : "13%"} {...fixedColCellStyle}>
                  {t('features.MasterMaintenance.AccountingOrganization.field.organizationId')}<br />
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterOrganizationId} onChange={setFilterOrganizationId} />
                  <ResizeHandle colIndex={0} setColWidths={setColWidths} />
                </Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left"
                  width={colWidths[1] ? `${colWidths[1]}px` : "17%"} position="relative">
                  {t('features.MasterMaintenance.AccountingOrganization.field.organizationName')}<br />
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterOrganizationName} onChange={setFilterOrganizationName} />
                  <ResizeHandle colIndex={1} setColWidths={setColWidths} />
                </Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left"
                  width={colWidths[2] ? `${colWidths[2]}px` : "17%"} position="relative">
                  {t('features.MasterMaintenance.AccountingOrganization.field.organizationNameEn')}<br />
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterOrganizationNameEn} onChange={setFilterOrganizationNameEn} />
                  <ResizeHandle colIndex={2} setColWidths={setColWidths} />
                </Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left"
                  width={colWidths[3] ? `${colWidths[3]}px` : "20%"} position="relative">
                  <HStack spacing={1} mb={0}>
                    <Text whiteSpace="nowrap">{t('features.MasterMaintenance.AccountingOrganization.field.parentOrganizationName')}</Text>
                    <HelpIcon size="14px" placement="bottom" width="280px">
                      <Text fontWeight="bold" mb={2}>{t('features.MasterMaintenance.AccountingOrganization.help.bgColor.title')}</Text>
                      <Text fontSize="sm">
                        {t('features.MasterMaintenance.AccountingOrganization.help.bgColor.description1')}<Box as="span" bg="red.100" color="red.600" px={1}>{t('features.MasterMaintenance.AccountingOrganization.help.bgColor.bgColorText')}</Box>{t('features.MasterMaintenance.AccountingOrganization.help.bgColor.description2')}
                      </Text>
                    </HelpIcon>
                  </HStack>
                  <DebouncedInput size="xs" placeholder={t('common.placeholder.filter')} mt={1} bg="white" fontWeight="normal" value={filterParentOrganizationId} onChange={setFilterParentOrganizationId} />
                  <ResizeHandle colIndex={3} setColWidths={setColWidths} />
                </Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left"
                  width={colWidths[4] ? `${colWidths[4]}px` : "12%"} position="relative">
                  {t('features.MasterMaintenance.AccountingOrganization.field.fiscalPeriod')}
                  <ResizeHandle colIndex={4} setColWidths={setColWidths} />
                </Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left"
                  width={colWidths[5] ? `${colWidths[5]}px` : "8%"} position="relative">
                  {t('common.label.deleteFlag')}
                  <ResizeHandle colIndex={5} setColWidths={setColWidths} />
                </Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left"
                  width={colWidths[6] ? `${colWidths[6]}px` : "13%"} position="relative">
                  {t('common.label.createDate')}
                  <ResizeHandle colIndex={6} setColWidths={setColWidths} />
                </Th>
                <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left"
                  width={colWidths[7] ? `${colWidths[7]}px` : "13%"} position="relative">
                  {t('common.label.updateDate')}
                  <ResizeHandle colIndex={7} setColWidths={setColWidths} />
                </Th>
              </Tr>
            </Thead>
            {tableBody}
          </Table>
        </TableContainer>
      </Box >

      {/* 編集モーダル */}
      <Modal isOpen={isOpenEditModal} onClose={onCloseEditModal} closeOnOverlayClick={false} onCloseComplete={handleCloseComplete} scrollBehavior="inside" size="3xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text>{t('features.MasterMaintenance.AccountingOrganization.modal.editTitle')}</Text>
          </ModalHeader>
          <ModalBody>
            <VStack>

              <FormControl isInvalid={errors.organizationId?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.AccountingOrganization.field.organizationId')}
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      value={editTarget.organizationId || ""}
                      onChange={(event) => { changeEditTarget("organizationId", event.target.value) }}
                      placeholder={t('features.MasterMaintenance.AccountingOrganization.placeholder.organizationIdExample')}
                      width={valueWidth}
                      maxLength={InputLimits.ACCOUNTING_ORG_ID}
                      isInvalid={errors.organizationId?.isError}
                      isDisabled={!!editTarget.updateDate}
                    />
                    <FormErrorMessage mt={0}>{errors.organizationId?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.fiscalPeriod?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    <HStack spacing={1}>
                      <Text>{t('features.MasterMaintenance.AccountingOrganization.field.fiscalPeriod')}</Text>
                      <HelpIcon size="20px" placement="right" width="500px">
                        <Text fontWeight="bold" mb={2}>{t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearChange.title')}</Text>
                        <UnorderedList>
                          <ListItem>{t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearChange.item1')}</ListItem>
                          <ListItem>{t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearChange.item2')}<br />{t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearChange.item2Rest')}</ListItem>
                          <ListItem>{t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearChange.item3')}<br />{t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearChange.item3Rest')}</ListItem>
                        </UnorderedList>
                      </HelpIcon>
                    </HStack>
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      type="month"
                      value={editTarget.fiscalPeriod || ""}
                      onChange={(e) => { changeEditTarget("fiscalPeriod", e.target.value) }}
                      width={valueWidth}
                      isInvalid={errors.fiscalPeriod?.isError}
                    />
                    {errors.fiscalPeriod?.isError ? (
                      <FormErrorMessage mt={0}>{errors.fiscalPeriod?.errorMessage}</FormErrorMessage>
                    ) : (
                      originalFiscalPeriod && editTarget.fiscalPeriod !== originalFiscalPeriod && (
                        <Text fontSize="xs" color="orange.500">
                          {t('features.MasterMaintenance.AccountingOrganization.help.fiscalYearChange.warning')}
                        </Text>
                      )
                    )}
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.organizationName?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.AccountingOrganization.field.organizationName')}
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      value={editTarget.organizationName || ""}
                      onChange={(event) => { changeEditTarget("organizationName", event.target.value) }}
                      placeholder={t('features.MasterMaintenance.AccountingOrganization.placeholder.organizationName')}
                      width={valueWidth}
                      maxLength={InputLimits.ACCOUNTING_ORG_NAME}
                      isInvalid={errors.organizationName?.isError}
                    />
                    <FormErrorMessage mt={0}>{errors.organizationName?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.organizationNameEn?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.AccountingOrganization.field.organizationNameEn')}
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      value={editTarget.organizationNameEn || ""}
                      onChange={(event) => { changeEditTarget("organizationNameEn", event.target.value) }}
                      placeholder={t('features.MasterMaintenance.AccountingOrganization.placeholder.organizationNameEn')}
                      width={valueWidth}
                      maxLength={InputLimits.ACCOUNTING_ORG_NAME_EN}
                      isInvalid={errors.organizationNameEn?.isError}
                    />
                    <FormErrorMessage mt={0}>{errors.organizationNameEn?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.parentOrganizationId?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.AccountingOrganization.field.parentOrganizationId')}
                  </FormLabel>
                  <VStack align="left" position="relative">
                    <Input
                      ref={parentOrgInputRef}
                      value={parentOrgInputValue}
                      onChange={(event) => {
                        const val = event.target.value;
                        setParentOrgInputValue(val);
                        changeEditTarget("parentOrganizationId", val);
                        setShowParentOrgSuggestions(true);
                      }}
                      onFocus={() => setShowParentOrgSuggestions(true)}
                      onBlur={() => {
                        // 候補クリックを処理するため少し遅延
                        setTimeout(() => setShowParentOrgSuggestions(false), 200);
                      }}
                      placeholder={t('features.MasterMaintenance.AccountingOrganization.placeholder.parentOrganizationIdOptional')}
                      width={valueWidth}
                      maxLength={InputLimits.ACCOUNTING_ORG_ID}
                      autoComplete="off"
                    />
                    {showParentOrgSuggestions && parentOrgSuggestions.length > 0 && (
                      <List
                        position="absolute"
                        top="40px"
                        width={valueWidth}
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        boxShadow="md"
                        zIndex={10}
                        maxH="200px"
                        overflowY="auto"
                        py={1}
                      >
                        {parentOrgSuggestions.map(m => (
                          <ListItem
                            key={m.organizationId}
                            px={3}
                            py={1.5}
                            cursor="pointer"
                            _hover={{ bg: "blue.50" }}
                            fontSize="sm"
                            onMouseDown={(e) => {
                              e.preventDefault(); // onBlurより先に処理
                              changeEditTarget("parentOrganizationId", m.organizationId!);
                              setParentOrgInputValue(m.organizationId!);
                              setShowParentOrgSuggestions(false);
                            }}
                          >
                            {m.organizationName}（{m.organizationId}）
                          </ListItem>
                        ))}
                      </List>
                    )}
                    <FormErrorMessage mt={0}>{errors.parentOrganizationId?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.deleted?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.deleteFlag')}
                  </FormLabel>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", false);
                        }}>{t('common.label.notDeleted')}</Button>

                      <Switch
                        isChecked={editTarget.deleted}
                        onChange={(event) => { changeEditTarget("deleted", event.target.checked) }}
                        isInvalid={errors.deleted?.isError}
                      />

                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", true)
                        }}>{t('common.label.deleted')}</Button>
                    </HStack>
                    <FormErrorMessage mt={0}>{errors.deleted?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.createDate?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.createDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.createDate)}</Text>
                    <FormErrorMessage mt={0}>{errors.createDate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.updateDate?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.updateDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.updateDate)}</Text>
                    <FormErrorMessage mt={0}>{errors.updateDate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
              e.currentTarget.blur();
              handleSave();
            }} m={1}>
              {t('common.button.save')}
            </Button>
            <Button onClick={handleClose} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* プレビュー表示モーダル */}
      <Modal isOpen={isOpenPreviewModal} onClose={handleClosePreviewModal} closeOnOverlayClick={true}>
        <ModalOverlay />
        <ModalContent minW="1500px">
          <ModalHeader>{t('features.MasterMaintenance.AccountingOrganization.previewModal.title')}</ModalHeader>
          <ModalBody>
            <HStack spacing={2} align="center" mb={2}>
              {/* 適用年度 */}
              <Text whiteSpace="nowrap">{t('features.MasterMaintenance.AccountingOrganization.label.fiscalYearColon')}</Text>
              <Input
                type="month"
                value={batchFiscalPeriod}
                onChange={async (e) => {
                  const newValue = e.target.value;
                  setBatchFiscalPeriod(newValue);

                  if ((newValue ?? '') === '') {
                    // 適用年度が空なら、紐づけ情報と既存データをリセット（statusはunmappedに戻す）
                    setImportData(prev => prev.map(item => ({ ...item, status: "unmapped" as const, mappingOrganizationId: undefined, mappingOrganizationName: undefined })));
                    setExistingData([]);
                    setInvalidateData([]);
                    setOriginalInvalidateIds(new Set());
                    return;
                  }

                  // 登録先適用年度の既存データを取得してマッチング実行
                  const fetchedExisting = await fetchOrganizationData(newValue, true) as AccountingOrganizationExistType[];
                  if (!fetchedExisting || fetchedExisting.length === 0) {
                    // 既存データなし（0件またはAPIエラー）→ すべて新規扱い
                    setImportData(prev => prev.map(item => ({ ...item, status: "new" as const, mappingOrganizationId: undefined, mappingOrganizationName: undefined })));
                    setExistingData([]);
                    setInvalidateData([]);
                    setOriginalInvalidateIds(new Set());
                    return;
                  }

                  // 適用年度変更時は前回の紐づけ情報をリセットしてから再マッチング
                  const resetImportData = importData.map(item => ({ ...item, status: undefined, mappingOrganizationId: undefined, mappingOrganizationName: undefined }));
                  const mapped = mappingOrganizationData(resetImportData, fetchedExisting);
                  setImportData(mapped);
                  setExistingData(fetchedExisting);
                  // インポートデータに紐づかなかった既存組織を無効化候補にセット
                  const usedIds = new Set(mapped.map(r => r.mappingOrganizationId).filter((id): id is string => !!id));
                  const candidates = fetchedExisting.filter(d => !d.deleted && !usedIds.has(d.organizationId ?? ''));
                  const candidateIds = new Set(candidates.map(c => c.organizationId ?? ''));
                  setInvalidateData(candidates.map(c => ({ ...c, excluded: false })));
                  setOriginalInvalidateIds(candidateIds);
                }}
                width="200px"
                size="sm"
              />
              <Text color="red">{t('features.MasterMaintenance.AccountingOrganization.previewModal.fiscalYearNote')}</Text>
            </HStack>
            {/* 件数表示 */}
            {batchFiscalPeriod && isPastFiscalYear(batchFiscalPeriod) && (
              <Text mt={4} color="red.500" fontWeight="bold">
                {t('features.MasterMaintenance.AccountingOrganization.dialog.bulkImport.pastYearWarning')}
              </Text>
            )}

            <Box borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.200" mt={2}>
              {/* タブ切り替え：インポートデータ ／ 無効化対象（候補が1件以上の場合のみタブ表示） */}
              <Tabs variant="line" colorScheme="blue">
                <TabList borderBottom="1px solid" borderColor="gray.200">
                  <Tab fontWeight="semibold" fontSize="sm">
                    {t('features.MasterMaintenance.AccountingOrganization.previewModal.tabImport')}
                    <Badge ml={2} mr={1} colorScheme="blue" pt={1} pb={1}>{importData.length}</Badge>
                    {t('features.MasterMaintenance.AccountingOrganization.previewModal.count')}
                  </Tab>
                  {invalidateData.length > 0 && (
                    <Tab fontWeight="semibold" fontSize="sm">
                      {t('features.MasterMaintenance.AccountingOrganization.previewModal.tabInvalidate')}
                      <Badge ml={2} mr={1} colorScheme="red" pt={1} pb={1}>{invalidateData.length}</Badge>
                      {t('features.MasterMaintenance.AccountingOrganization.previewModal.count')}
                    </Tab>
                  )}
                </TabList>
                <TabPanels>
                  {/* タブ1：インポートデータ一覧 */}
                  <TabPanel p={0} pt={2}>
                    <TableContainer overflowY="auto" overflowX="auto" h="calc(100dvh - 440px)">
                      <Table border="1px solid" borderColor="gray.200" variant="simple" size="sm" sx={{
                        tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0,
                      }}
                        width={previewColWidths.length > 0 ? `${previewColWidths.reduce((a, b) => a + b, 0) - 15}px` : "1435px"}
                      >
                        <Thead position="sticky" top={0} zIndex={1}>
                          <Tr bgColor="gray.50">
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="left" position="relative"
                              width={previewColWidths[0] ? `${previewColWidths[0]}px` : "10%"}>
                              {t('features.MasterMaintenance.AccountingOrganization.previewModal.columnStatus')}
                              <ResizeHandle colIndex={0} setColWidths={setPreviewColWidths} />
                            </Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="left" position="relative"
                              width={previewColWidths[1] ? `${previewColWidths[1]}px` : "15%"}>
                              {t('features.MasterMaintenance.AccountingOrganization.field.organizationId')}
                              <ResizeHandle colIndex={1} setColWidths={setPreviewColWidths} />
                            </Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="left" position="relative"
                              width={previewColWidths[2] ? `${previewColWidths[2]}px` : "20%"}>
                              {t('features.MasterMaintenance.AccountingOrganization.field.organizationName')}
                              <ResizeHandle colIndex={2} setColWidths={setPreviewColWidths} />
                            </Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="left" position="relative"
                              width={previewColWidths[3] ? `${previewColWidths[3]}px` : "20%"}>
                              {t('features.MasterMaintenance.AccountingOrganization.field.organizationNameEn')}
                              <ResizeHandle colIndex={3} setColWidths={setPreviewColWidths} />
                            </Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="left" position="relative"
                              width={previewColWidths[4] ? `${previewColWidths[4]}px` : "20%"}>
                              {t('features.MasterMaintenance.AccountingOrganization.field.parentOrganizationName')}
                              <ResizeHandle colIndex={4} setColWidths={setPreviewColWidths} />
                            </Th>
                            <Th borderRight="1px solid" borderColor="gray.200" textAlign="left" position="relative"
                              width={previewColWidths[5] ? `${previewColWidths[5]}px` : "15%"}>
                              {t('features.MasterMaintenance.AccountingOrganization.previewModal.columnMapping')}
                              <ResizeHandle colIndex={5} setColWidths={setPreviewColWidths} />
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {importData.map((row, rowIndex) => {

                            const judgeBgColor = () => {
                              if ((batchFiscalPeriod ?? '') === '' || row.status === "unmapped") {
                                // 適用年度未入力 or 紐づけ未設定 → 赤
                                return 'red.100 !important';
                              }
                              if (row.status === "new") {
                                // 新規登録 → 緑
                                return 'green.50 !important';
                              }
                              return '';
                            }

                            const parentDisplayText = (() => {
                              if (!row.parentOrganizationId) return '';
                              const parentName = importOrgNameMap.get(row.parentOrganizationId) ?? row.parentOrganizationId;
                              if (isHierarchyFormat) {
                                const parentRealId = importIdToRealIdMap.get(row.parentOrganizationId) ?? t('common.label.autoNumber');
                                return `${parentName} (${parentRealId})`;
                              }
                              return `${parentName} (${row.parentOrganizationId})`;
                            })();

                            return (
                              <Tr key={rowIndex} _hover={{ bg: "gray.50" }}>
                                <Td borderRight="1px solid" borderColor="gray.200" bg={judgeBgColor()} {...ellipsisStyle}>{row.status ? t(`features.MasterMaintenance.AccountingOrganization.previewModal.status.${row.status}`) : ''}</Td>
                                <Td borderRight="1px solid" borderColor="gray.200" bg={judgeBgColor()} {...ellipsisStyle}>
                                  {isHierarchyFormat
                                    ? (row.status === "overwrite"
                                      ? row.mappingOrganizationId
                                      : t('common.label.autoNumber'))
                                    : row.organizationId}
                                </Td>
                                <Td borderRight="1px solid" borderColor="gray.200" bg={judgeBgColor()} {...ellipsisStyle}>{row.organizationName}</Td>
                                <Td borderRight="1px solid" borderColor="gray.200" bg={judgeBgColor()} {...ellipsisStyle}>{row.organizationNameEn}</Td>
                                <Td borderRight="1px solid" borderColor="gray.200" bg={judgeBgColor()} {...ellipsisStyle} title={parentDisplayText}>
                                  {parentDisplayText}
                                </Td>
                                <Td borderRight="1px solid" borderColor="gray.200" bg={judgeBgColor()} p={2} overflow="hidden">
                                  <HStack spacing={2} overflow="hidden">
                                    <Text {...ellipsisStyle} flex={1}>{row.mappingOrganizationName ?? t('features.MasterMaintenance.AccountingOrganization.preview.noMapping')}</Text>
                                    <Button
                                      size="xs"
                                      flexShrink={0}
                                      onClick={() => handleOpenMappingModal(rowIndex)}
                                      isDisabled={existingData.length === 0}
                                    >
                                      {row.mappingOrganizationId
                                        ? t('common.button.change')
                                        : t('features.MasterMaintenance.AccountingOrganization.preview.setButton')
                                      }
                                    </Button>
                                  </HStack>
                                </Td>
                              </Tr>
                            )
                          })}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </TabPanel>

                  {/* タブ2：無効化対象一覧（候補が1件以上の場合のみ） */}
                  {invalidateData.length > 0 && (
                    <TabPanel p={0} pt={2}>
                      <TableContainer overflowY="auto" overflowX="auto" h="calc(100dvh - 440px)">
                        <Table border="1px solid" borderColor="gray.200" variant="simple" size="sm" sx={{ tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0, }}
                          width={invalidateColWidths.length > 0 ? `${invalidateColWidths.reduce((a, b) => a + b, 0) - 15}px` : "1435px"}
                        >
                          <Thead position="sticky" top={0} zIndex={1}>
                            <Tr bgColor="gray.50">
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" position="relative"
                                width={invalidateColWidths[0] ? `${invalidateColWidths[0]}px` : "15%"}>
                                {t('features.MasterMaintenance.AccountingOrganization.previewModal.columnStatus')}
                                <ResizeHandle colIndex={0} setColWidths={setInvalidateColWidths} />
                              </Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" position="relative"
                                width={invalidateColWidths[1] ? `${invalidateColWidths[1]}px` : "20%"}>
                                {t('features.MasterMaintenance.AccountingOrganization.field.organizationId')}
                                <ResizeHandle colIndex={1} setColWidths={setInvalidateColWidths} />
                              </Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" position="relative"
                                width={invalidateColWidths[2] ? `${invalidateColWidths[2]}px` : "30%"}>
                                {t('features.MasterMaintenance.AccountingOrganization.field.organizationName')}
                                <ResizeHandle colIndex={2} setColWidths={setInvalidateColWidths} />
                              </Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" position="relative"
                                width={invalidateColWidths[3] ? `${invalidateColWidths[3]}px` : "25%"}>
                                {t('features.MasterMaintenance.AccountingOrganization.field.organizationNameEn')}
                                <ResizeHandle colIndex={3} setColWidths={setInvalidateColWidths} />
                              </Th>
                              <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" position="relative"
                                width={invalidateColWidths[4] ? `${invalidateColWidths[4]}px` : "10%"}>
                                <HStack spacing={2}>
                                  {/* 一括チェック: 全行の「無効化しない」を一括ON/OFF（一部のみONの場合は中間状態） */}
                                  <input
                                    type="checkbox"
                                    checked={invalidateData.length > 0 && invalidateData.every(r => r.excluded)}
                                    ref={(el) => {
                                      if (el) {
                                        const excludedCount = invalidateData.filter(r => r.excluded).length;
                                        el.indeterminate = excludedCount > 0 && excludedCount < invalidateData.length;
                                      }
                                    }}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setInvalidateData(prev => prev.map(r => ({ ...r, excluded: checked })));
                                    }}
                                  />
                                  <Text as="span">
                                    {t('features.MasterMaintenance.AccountingOrganization.previewModal.keepOrg')}
                                  </Text>
                                </HStack>
                                <ResizeHandle colIndex={4} setColWidths={setInvalidateColWidths} />
                              </Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {invalidateData.map((row, idx) => {
                              const rowBg = row.excluded ? "#f0fff4" : "#fff5f5";
                              const tdStyle: React.CSSProperties = { backgroundColor: rowBg };
                              return (
                                <Tr key={row.organizationId ?? idx} _hover={{ bg: "gray.50" }}>
                                  <Td borderRight="1px solid" borderColor="gray.200" style={tdStyle} {...ellipsisStyle}>
                                    {row.excluded ? (
                                      // チェックON（無効化しない）→「無効化しない」を緑文字で表示
                                      <Text as="span" color="green.600" fontWeight="bold" fontSize="sm">
                                        {t('features.MasterMaintenance.AccountingOrganization.previewModal.status.doNotInvalidate')}
                                      </Text>
                                    ) : (
                                      // チェックOFF（無効化する）→「無効」を赤文字で表示
                                      <Text as="span" color="red.500" fontWeight="bold" fontSize="sm">
                                        {t('features.MasterMaintenance.AccountingOrganization.previewModal.status.invalid')}
                                      </Text>
                                    )}
                                  </Td>
                                  <Td borderRight="1px solid" borderColor="gray.200" style={tdStyle} color={row.excluded ? "gray.700" : "gray.500"} {...ellipsisStyle}>{row.organizationId}</Td>
                                  <Td borderRight="1px solid" borderColor="gray.200" style={tdStyle} color={row.excluded ? "gray.700" : "gray.500"} {...ellipsisStyle}>{row.organizationName}</Td>
                                  <Td borderRight="1px solid" borderColor="gray.200" style={tdStyle} color={row.excluded ? "gray.700" : "gray.500"} {...ellipsisStyle}>{row.organizationNameEn}</Td>
                                  <Td borderRight="1px solid" borderColor="gray.200" style={tdStyle} textAlign="center">
                                    <input
                                      type="checkbox"
                                      checked={!!row.excluded}
                                      onChange={(e) => {
                                        setInvalidateData(prev => prev.map((d, i) =>
                                          i === idx ? { ...d, excluded: e.target.checked } : d
                                        ));
                                      }}
                                    />
                                  </Td>
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </TabPanel>
                  )}
                </TabPanels>
              </Tabs>
            </Box>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button ref={cancelRef} onClick={handleClosePreviewModal}>
                {t('common.button.cancel')}
              </Button>
              <Button
                colorScheme='blue'
                onClick={onOpenBulkConfirm}
                ml={3}
                isDisabled={
                  (batchFiscalPeriod ?? '') === '' ||
                  importData.length === 0 ||
                  importData.some(row => row.status === "unmapped")
                }
              >
                {t('features.MasterMaintenance.AccountingOrganization.dialog.bulkImport.execRegister')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 既存データの紐づけモーダル */}
      <Modal isOpen={isOpenMappingModal} onClose={onCloseMappingModal} closeOnOverlayClick={false} blockScrollOnMount={false}>
        <ModalOverlay />
        <ModalContent minW="700px">
          <ModalHeader>
            {t('features.MasterMaintenance.AccountingOrganization.mappingModal.title')}
            {mappingTargetIndex >= 0 && (
              <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
                {t('features.MasterMaintenance.AccountingOrganization.mappingModal.target')}
                {importData[mappingTargetIndex]?.organizationName}
              </Text>
            )}
          </ModalHeader>
          <ModalBody>
            {(() => {
              // 他行で紐づけ済みの組織IDセット（自分の行は除外しない）
              const usedIds = new Set<string>(
                importData
                  .filter((_, i) => i !== mappingTargetIndex)
                  .map(r => r.mappingOrganizationId)
                  .filter((id): id is string => !!id)
              );
              // 組織ID → 組織名のマップ（親組織名の動的解決用）
              const existingOrgNameMap = new Map(
                existingData.map(d => [d.organizationId, d.organizationName])
              );
              const filteredExisting = existingData.filter(d => !d.deleted && !usedIds.has(d.organizationId ?? ''));
              return (
                <TableContainer overflowY="auto" h="calc(100dvh - 380px)" onWheel={(e) => e.stopPropagation()}>
                  <Table border="1px solid" borderColor="gray.200" variant="simple" size="sm" sx={{ tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0, }}>
                    <Thead position="sticky" top={0} zIndex={1}>
                      <Tr bgColor="gray.50">
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" position="relative"
                          width={mappingColWidths[0] ? `${mappingColWidths[0]}px` : "40px"}>
                          <ResizeHandle colIndex={0} setColWidths={setMappingColWidths} />
                        </Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" position="relative"
                          width={mappingColWidths[1] ? `${mappingColWidths[1]}px` : "25%"}>
                          {t('features.MasterMaintenance.AccountingOrganization.field.organizationId')}
                          <ResizeHandle colIndex={1} setColWidths={setMappingColWidths} />
                        </Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" position="relative"
                          width={mappingColWidths[2] ? `${mappingColWidths[2]}px` : "37%"}>
                          {t('features.MasterMaintenance.AccountingOrganization.field.organizationName')}
                          <ResizeHandle colIndex={2} setColWidths={setMappingColWidths} />
                        </Th>
                        <Th borderRight="1px solid" borderColor="gray.200" bgColor="gray.50" textAlign="left" position="relative"
                          width={mappingColWidths[3] ? `${mappingColWidths[3]}px` : "38%"}>
                          {t('features.MasterMaintenance.AccountingOrganization.field.parentOrganizationName')}
                          <ResizeHandle colIndex={3} setColWidths={setMappingColWidths} />
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {/* 「該当なし」固定行 */}
                      <Tr
                        cursor="pointer"
                        bg={mappingSelectedId === "" ? "blue.50" : "white"}
                        _hover={{ bg: "gray.100" }}
                        onClick={() => setMappingSelectedId("")}
                      >
                        <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                          <input type="radio" readOnly checked={mappingSelectedId === ""} />
                        </Td>
                        <Td borderRight="1px solid" borderColor="gray.200" colSpan={3} fontStyle="italic" color="gray.500" {...ellipsisStyle}>
                          {t('features.MasterMaintenance.AccountingOrganization.mappingModal.noMapping')}
                        </Td>
                      </Tr>
                      {/* 既存データ一覧 */}
                      {filteredExisting.map((existing) => (
                        <Tr
                          key={existing.organizationId}
                          cursor="pointer"
                          bg={mappingSelectedId === existing.organizationId ? "blue.50" : "white"}
                          _hover={{ bg: "gray.100" }}
                          onClick={() => setMappingSelectedId(existing.organizationId ?? "")}
                        >
                          <Td borderRight="1px solid" borderColor="gray.200" textAlign="center">
                            <input type="radio" readOnly checked={mappingSelectedId === existing.organizationId} />
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200" {...ellipsisStyle}>{existing.organizationId}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200" {...ellipsisStyle}>{existing.organizationName}</Td>
                          <Td borderRight="1px solid" borderColor="gray.200" {...ellipsisStyle}>{existingOrgNameMap.get(existing.parentOrganizationId ?? "") ?? ""}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              );
            })()}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onCloseMappingModal} mr={3}>
              {t('common.button.cancel')}
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmMapping}>
              {t('common.button.confirmAction')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 過去年度編集確認ダイアログ */}
      <AlertDialog
        isOpen={isPastYearAlertOpen}
        leastDestructiveRef={pastYearCancelRef}
        onClose={onClosePastYearAlert}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              {t('features.MasterMaintenance.AccountingOrganization.dialog.pastYear.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text color="red.500" fontWeight="bold">
                {t('features.MasterMaintenance.AccountingOrganization.dialog.pastYear.warning', { fiscalPeriod: editTarget.fiscalPeriod || selectedFiscalPeriod })}
              </Text>
              <Text mt={4}>{t('features.MasterMaintenance.AccountingOrganization.dialog.pastYear.confirmSave')}</Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={pastYearCancelRef} onClick={onClosePastYearAlert}>
                {t('common.button.cancel')}
              </Button>
              <Button colorScheme='red' onClick={handleConfirmPastYearSave} ml={3}>
                {t('features.MasterMaintenance.AccountingOrganization.dialog.pastYear.execSave')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Excel一括登録 実行確認ダイアログ */}
      <AlertDialog
        isOpen={isBulkConfirmOpen}
        leastDestructiveRef={bulkConfirmCancelRef}
        onClose={onCloseBulkConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              {t('features.MasterMaintenance.AccountingOrganization.dialog.bulkImport.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('features.MasterMaintenance.AccountingOrganization.dialog.bulkImport.confirmExec')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={bulkConfirmCancelRef} onClick={onCloseBulkConfirm}>
                {t('common.button.cancel')}
              </Button>
              <Button
                colorScheme='blue'
                ml={3}
                onClick={() => {
                  onCloseBulkConfirm();
                  handleBatchImport();
                }}
              >
                {t('features.MasterMaintenance.AccountingOrganization.dialog.bulkImport.execRegister')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default AccountingOrganization;
